import React from "react"
import autobind from "class-autobind"
import { Paper, TextField, Toggle, FontIcon, SelectField, MenuItem } from "material-ui"
import { IJobDocument, EAddJobValidationType, IOccupationInfo, ISpecialtyInfo, IBadgeInfo, IClientDocument, IJobStatus, EJobType, IDate, IUIUser } from "../../../models"
import strings from "../../../localization/strings"
import ChooseHierarchyChip from "../../../components/hierarchy/choose_hierarchy/ChooseHierarchyChip"
import AccountStore from "../../../stores/AccountStore"
import ChooseOccupationChip from "../../../components/hr_settings/choose_occupation/ChooseOccupationChip"
import ChooseSpecialtyChip from "../../../components/hr_settings/choose_specialty/ChooseSpecialtyChip"
import ChooseBadgeChip from "../../../components/hr_settings/choose_badge/ChooseBadgeChip"
import ChooseClientChip from "../../../components/clients/choose_client/ChooseClientChip"
import DateRangeSelect from "../../../components/common/DateRangeSelect"
import DateTimeRangeSelect from "../../../components/common/DateTimeRangeSelect"
import _ from "lodash"
import ClearIcon from "material-ui/svg-icons/content/clear"
import * as api from "../../../api"
import * as timeHelper from "../../../helpers/timeHelper"
import * as jobHelper from "../../../helpers/jobHelper"
import cloneDeep from "lodash/cloneDeep"
import moment from "moment"
import AppStateStore from "../../../stores/AppStateStore"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"
import JobHierarchyMoveWarningDialog from "../../../components/job/JobHierarchyMoveWarningDialog"
interface IProps {
    readonly: boolean
    job: IJobDocument
    jobStatuses: IJobStatus[]
    handleUpdateJob(job: IJobDocument): void
    reloadJob(): void
}
interface IState {
    expanded: boolean
    jobName: string
    errorName?: string
    hierarchyId: string
    active: boolean
    userModified: boolean
    jobId: string
    errorJobId?: string
    clientId: string
    clientName: string
    costCode: string
    errorCostCode?: string
    hasDates: boolean
    // these are local time
    startDate: IDate
    endDate: IDate
    note: string
    jobOccupations: IOccupationInfo[]
    jobSpecialties: ISpecialtyInfo[]
    jobBadges: IBadgeInfo[]
    status: IJobStatus
    statusValue: { Id: string, Name: string }
    subStatusValue: { Id: string, Name: string }
    autoAssignEmployees: boolean
    showJobToMobileUsers: boolean
    jobType: EJobType
    field1: string
    field2: string
    field3: string
    field4: string
    hierarchyMoveValidationResult: IUIUser[]
}

export default class JobDetailsSummary extends React.Component<IProps, IState> {
    private updateParamDateTimeChanged: boolean
    private originalHierarchyId: string
    constructor(props: IProps) {
        super(props)
        this.state = {
            expanded: false,
            jobName: props.job.Name,
            errorName: "",
            hierarchyId: props.job.HierarchyId,
            active: props.job.Active,
            userModified: props.job.UserModified,
            jobId: props.job.ExternalId ? props.job.ExternalId : "",
            errorJobId: "",
            clientId: props.job.ClientId ? props.job.ClientId : null,
            clientName: props.job.ClientName ? props.job.ClientName : null,
            costCode: props.job.CostCode ? props.job.CostCode : "",
            errorCostCode: "",
            hasDates: props.job.HasStartEndDates,
            startDate: timeHelper.convertDefaultDateToNull(props.job.StartDateLocal) === null ? moment().startOf("day").format() : moment(timeHelper.keepConvertUTCToLocal(props.job.StartDateLocal)).format(),
            endDate: timeHelper.convertDefaultDateToNull(props.job.EndDateLocal) === null ? moment().add(7, "days").endOf("day").format() : moment(timeHelper.keepConvertUTCToLocal(props.job.EndDateLocal)).format(),
            note: props.job.Note ? props.job.Note : "",
            jobOccupations: props.job.RequiredOccupations,
            jobSpecialties: props.job.RequiredSpecialties,
            jobBadges: props.job.Badges,
            status: props.job.Status,
            statusValue: { Id: props.job.Status ? props.job.Status.Id : "", Name: props.job.Status ? props.job.Status.Name : "" },
            subStatusValue: { Id: (props.job.Status && props.job.Status.JobSubStatus) ? props.job.Status.JobSubStatus.Id : "", Name: (props.job.Status && props.job.Status.JobSubStatus) ? props.job.Status.JobSubStatus.Name : "" },
            autoAssignEmployees: props.job.AutoAssignEmployees || false,
            showJobToMobileUsers: props.job.ShowJobToMobileUsers || false,
            jobType: (props.job.JobType === EJobType.NonContract || props.job.JobType === EJobType.Contract) ? props.job.JobType : EJobType.NonContract,
            field1: props.job.Field1 || "",
            field2: props.job.Field2 || "",
            field3: props.job.Field3 || "",
            field4: props.job.Field4 || "",
            hierarchyMoveValidationResult: []
        }
        autobind(this)
    }

    componentWillReceiveProps(nextProps: IProps) {
        this.props.job.UserModified !== nextProps.job.UserModified &&
            this.setState({ userModified: nextProps.job.UserModified })
    }

    getJobPayload() {
        const payload = {
            Id: this.props.job.Id,
            AccountId: AccountStore.getCurrentAccount().Id,
            Name: this.state.jobName.trim(),
            HierarchyId: this.state.hierarchyId ? this.state.hierarchyId : null,
            Active: this.state.active,
            CostCode: this.state.costCode.trim(),
            ExternalId: this.state.jobId.trim(),
        } as IJobDocument
        return payload
    }

    async handleUpdateJobData(dateTimeChanged = false) {
        const error = await this.checkValidate()
        if (error) return
        this.updateParamDateTimeChanged = dateTimeChanged
        const hierarchyId = this.state.hierarchyId ? this.state.hierarchyId : null
        if (this.props.job.HierarchyId !== hierarchyId) {
            const hierarchyMoveValidationResultRes = await api.checkJobHierarchyMoveValidatation(this.getJobPayload())
            const hierarchyMoveValidationResult = hierarchyMoveValidationResultRes.data as IUIUser[]
            if (hierarchyMoveValidationResult.length > 0) {
                this.setState({ hierarchyMoveValidationResult })
                return
            }
        }
        this.updateJob()
    }

    updateJob() {
        const job: IJobDocument = cloneDeep(this.props.job)
        job.Name = this.state.jobName.trim()
        job.ExternalId = this.state.jobId.trim() ? this.state.jobId.trim() : null
        job.Active = this.state.active
        job.ClientId = this.state.clientId
        job.ClientName = this.state.clientName
        job.CostCode = this.state.costCode.trim() ? this.state.costCode.trim() : null
        job.HasStartEndDates = this.state.hasDates
        job.Note = this.state.note
        job.HierarchyId = this.state.hierarchyId ? this.state.hierarchyId : null
        job.AutoAssignEmployees = this.state.autoAssignEmployees
        job.ShowJobToMobileUsers = this.state.showJobToMobileUsers
        job.Field1 = this.state.field1 !== "" ? this.state.field1 : null
        job.Field2 = this.state.field2 !== "" ? this.state.field2 : null
        job.Field3 = this.state.field3 !== "" ? this.state.field3 : null
        job.Field4 = this.state.field4 !== "" ? this.state.field4 : null
        if (AccountStore.getClientsEnabled()) {
            if (this.updateParamDateTimeChanged) {
                job.StartDateLocal = this.state.hasDates ? moment(this.state.startDate).utc(true).format() : null
                job.EndDateLocal = this.state.hasDates ? moment(this.state.endDate).utc(true).format() : null
            }
            job.RequiredOccupations = this.state.jobOccupations
            job.RequiredSpecialties = this.state.jobSpecialties
            job.Badges = this.state.jobBadges
            job.Status = this.state.status
        }
        if (this.canToggleJobType()) job.JobType = this.state.jobType

        this.props.handleUpdateJob(job)
    }

    async checkValidate() {
        this.setState({ errorName: "", errorJobId: "", errorCostCode: "" })
        let error = false
        const res = await api.checkValidateJob(this.getJobPayload())
        const result = res.data as EAddJobValidationType[]

        if (!this.state.jobName.trim()) {
            this.setState({ errorName: strings.NameRequired })
            error = true
        }
        if (result.includes(EAddJobValidationType.DuplicatedName) && this.props.job.Name.trim() !== this.state.jobName.trim()) {
            this.setState({
                errorName: strings.Name_is_duplicated
            })
            error = true
        }
        if (result.includes(EAddJobValidationType.DuplicatedExternalId) && this.props.job.ExternalId.trim() !== this.state.jobId.trim()) {
            this.setState({
                errorJobId: strings.Job_ID_is_duplicated
            })
            error = true
        }
        if (result.includes(EAddJobValidationType.DuplicatedCostCode)) {
            this.setState({
                errorCostCode: strings.Cost_Code_is_duplicated
            })
            error = true
        }
        return error
    }

    canToggleJobType(): boolean {
        return AccountStore.getJobsEnabled() && AccountStore.getCRMEnabled() && AccountStore.getHREnabled()
    }

    saveJob = _.debounce(() => this.handleUpdateJobData(), 1000)

    render() {
        const editCutomFieldsAllowed = permissionHelper.hasPermission(AuthenticationStore.Permissions.Jobs.EditCustomJobFields)
        const allowClientSelectionEdits = permissionHelper.hasPermission(AuthenticationStore.Permissions.Jobs.EditJobClientSelection)
        const customFieldLabels = AccountStore.getCurrentAccountSettings() && AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobCustomLabels
        const noteItem =
            <TextField
                data-intercom-target={"menu-Jobs-Entry-Notes"}
                multiLine
                rows={3}
                rowsMax={4}
                floatingLabelText={strings.Note}
                value={this.state.note}
                style={{ width: 350 }}
                onChange={event => this.setState({ note: event.target.value })}
                onBlur={() => this.handleUpdateJobData()}
                disabled={this.props.readonly}
            />
        
        const costCodeItem =
            <TextField
                data-intercom-target={"menu-Jobs-Entry-CostCode"}
                floatingLabelText={strings.Cost_Code}
                value={this.state.costCode}
                errorText={this.state.errorCostCode}
                errorStyle={{ position: "absolute", bottom: -10 }}
                style={{ width: 350 }}
                onChange={event => this.setState({ costCode: event.target.value }, () => this.checkValidate())}
                onBlur={() => this.handleUpdateJobData()}
                disabled={this.props.readonly}
            />
        const statusAndSubStatusItem =
            <div style={{ width: 350, display: "flex", marginTop: 45 }}>
                <SelectField
                    floatingLabelText={strings.Status}
                    value={this.state.statusValue ? this.state.statusValue.Id : ""}
                    onChange={(event, key, value) => {
                        const status = this.props.jobStatuses.find((status) => status.Id === value)
                        const statusValue = { Id: status.Id, Name: status.Name }
                        this.setState({ statusValue, status }, () => this.handleUpdateJobData())
                    }}
                    style={{ marginRight: 8 }}
                    disabled={this.props.readonly}
                >
                    {this.props.jobStatuses
                        .map((item, index) =>
                            <MenuItem key={index} value={item.Id} primaryText={item.Name} />
                        )
                    }
                </SelectField>
                <SelectField
                    floatingLabelText={strings.Substatus}
                    value={this.state.subStatusValue ? this.state.subStatusValue.Id : ""}
                    onChange={(event, key, value) => {
                        const status = this.state.status
                        const subStatus = this.state.status && this.state.status.SubStatuses.find((subStatus) => subStatus.Id === value)
                        const subStatusValue = { Id: subStatus.Id, Name: subStatus.Name }
                        status.JobSubStatus = subStatus
                        this.setState({ status, subStatusValue }, () => this.handleUpdateJobData())
                    }}
                    disabled={this.state.status === null || this.state.status === undefined ? true : this.props.readonly}
                >
                    {this.state.status && this.state.status.SubStatuses
                        .filter(i => i.Active)
                        .map((item, index) =>
                            <MenuItem key={index} value={item.Id} primaryText={item.Name} />
                        )
                    }
                </SelectField>
                {(this.state.status !== null) && (
                    <ClearIcon
                        style={{ marginLeft: "1%", marginTop: "11%", cursor: "pointer" }}
                        onClick={() => this.setState({ status: null, statusValue: null, subStatusValue: null }, () => this.handleUpdateJobData())}
                    />
                )}
            </div>
        return (
            <Paper
                zDepth={1}
                style={{ textAlign: "left", margin: 20, position: "relative", overFlow: "hidden", padding: 20 }}
            >
                <div style={{ fontSize: 25, fontWeight: "bold", position: "relative" }}>
                    {jobHelper.checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} Details` : strings.Job_Details}
                    <FontIcon
                        data-intercom-target={"menu-Jobs-Btn-OpenJobNotes"}
                        className="fa fa-info-circle"
                        style={{ fontSize: 20, color: "#777777", cursor: "pointer", position: "absolute", top: -9 }}
                        onTouchTap={() => AppStateStore.setNoteDrawerContent({
                            drawerTitle: `${this.props.job ? this.props.job.Name : ""}`,
                            objectData: {
                                ObjectId: this.props.job && this.props.job.Id
                            },
                            objectName: this.props.job.Name,
                            dataType: "job",
                        })}
                    />
                </div>
                {this.state.expanded &&
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                            <TextField
                                data-intercom-target={"menu-Jobs-Entry-JobName"}
                                floatingLabelText={jobHelper.checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} Name` : strings.Job_Name}
                                value={this.state.jobName}
                                title={this.state.jobName}
                                errorText={this.state.errorName}
                                errorStyle={{ position: "absolute", bottom: -10 }}
                                style={{ width: 350 }}
                                onChange={event => this.setState({ jobName: event.target.value }, () => this.checkValidate())}
                                onBlur={() => this.handleUpdateJobData()}
                                disabled={this.props.readonly}
                            />
                            <TextField
                                data-intercom-target={"menu-Jobs-Entry-JobId"}
                                floatingLabelText={jobHelper.checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} Id`: strings.Job_Id}
                                value={this.state.jobId}
                                errorText={this.state.errorJobId}
                                errorStyle={{ position: "absolute", bottom: -10 }}
                                style={{ width: 350 }}
                                onChange={event => this.setState({ jobId: event.target.value }, () => this.checkValidate())}
                                onBlur={() => this.handleUpdateJobData()}
                                disabled={this.props.readonly}
                            />
                            <div style={{ width: 350 }}>
                                {AccountStore.getClientsEnabled() ?
                                    <ChooseClientChip
                                        disabled={allowClientSelectionEdits ? this.props.readonly : true}
                                        selectedClient={this.state.clientId ? { Id: this.state.clientId, Name: this.state.clientName } as IClientDocument : null}
                                        handleSave={(client: IClientDocument) => {
                                            if (client) this.setState({ clientId: client.Id, clientName: client.Name }, () => this.handleUpdateJobData())
                                            else this.setState({ clientId: null, clientName: null }, () => this.handleUpdateJobData())
                                        }}
                                    />
                                    :
                                    costCodeItem
                                }
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: !AccountStore.getCRMEnabled() ? "center" : "unset" }}>
                            <div style={{ width: 350, marginTop: 25 }}>
                                <Toggle
                                    data-intercom-target={"menu-Jobs-Toggle-Active"}
                                    onToggle={(_e, active) => {
                                        if (active) {
                                            this.setState({ active }, () => this.handleUpdateJobData())
                                        }
                                        else {
                                            AppStateStore.setProgressSnackbarContent("loading")
                                            api.allowDeleteOrInactiveJobs([this.props.job.Id]).then(res => {
                                                AppStateStore.setProgressSnackbarContent(null)
                                                res.data ?
                                                    this.setState({ active }, () => this.handleUpdateJobData()) :
                                                    AppStateStore.setAlertDialogContent({
                                                        title: strings.Cannot_Inactive_Job,
                                                        content: strings.Cannot_Inactive_Job_because_it_is_already_use
                                                    })
                                            })
                                        }
                                    }}
                                    style={{ width: 100, marginTop: 10 }}
                                    toggled={this.state.active}
                                    label={strings.Active}
                                    disabled={this.props.readonly}
                                />
                                <Toggle
                                    data-intercom-target={"menu-Jobs-Toggle-UserModified"}
                                    onToggle={(_e, userModified) => this.setState({ userModified }, () => api.updateJobUserMofifiedFlag(this.props.job.Id, this.state.userModified).then(() => this.props.reloadJob()))}
                                    style={{ width: 150, marginTop: 10 }}
                                    toggled={this.state.userModified}
                                    label={strings.UserModified}
                                    disabled={this.props.readonly}
                                />
                                <div style={{ display: "flex" }}>
                                    <div>
                                        <TextField
                                            floatingLabelText={(customFieldLabels.Field1Label === null || customFieldLabels.Field1Label === undefined || customFieldLabels.Field1Label === "") ? `${strings.Custom_Field} 1` : customFieldLabels.Field1Label}
                                            value={this.state.field1}
                                            onChange={e => this.setState({ field1: e.target.value }, () => this.saveJob())}
                                            style={{ width: 165 }}
                                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                                            disabled={editCutomFieldsAllowed ? this.props.readonly : true}
                                        />
                                        <TextField
                                            floatingLabelText={(customFieldLabels.Field2Label === null || customFieldLabels.Field2Label === undefined || customFieldLabels.Field2Label === "") ? `${strings.Custom_Field} 2` : customFieldLabels.Field2Label}
                                            value={this.state.field2}
                                            onChange={e => this.setState({ field2: e.target.value }, () => this.saveJob())}
                                            style={{ width: 165 }}
                                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                                            disabled={editCutomFieldsAllowed ? this.props.readonly : true}
                                        />
                                    </div>
                                    <div>
                                        <TextField
                                            floatingLabelText={(customFieldLabels.Field3Label === null || customFieldLabels.Field3Label === undefined || customFieldLabels.Field3Label === "") ? `${strings.Custom_Field} 3` : customFieldLabels.Field3Label}
                                            value={this.state.field3}
                                            onChange={e => this.setState({ field3: e.target.value }, () => this.saveJob())}
                                            style={{ width: 165 }}
                                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                                            disabled={editCutomFieldsAllowed ? this.props.readonly : true}
                                        />
                                        <TextField
                                            floatingLabelText={(customFieldLabels.Field4Label === null || customFieldLabels.Field4Label === undefined || customFieldLabels.Field4Label === "") ? `${strings.Custom_Field} 4` : customFieldLabels.Field4Label}
                                            value={this.state.field4}
                                            onChange={e => this.setState({ field4: e.target.value }, () => this.saveJob())}
                                            style={{ width: 165 }}
                                            underlineDisabledStyle={{ borderBottom: "1px solid #e0e0e0" }}
                                            disabled={editCutomFieldsAllowed ? this.props.readonly : true}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{ width: 350 }}>
                                {AccountStore.getCRMEnabled() ?
                                    costCodeItem : AccountStore.getClientsEnabled() ?
                                        [costCodeItem, noteItem] : noteItem
                                }
                            </div>
                            <div style={{ width: 350, marginTop: 7 }}>
                                <ChooseHierarchyChip
                                    readonly={this.props.readonly}
                                    chipLabel={strings.Hierarchy}
                                    hierarchyItemId={this.state.hierarchyId}
                                    chipMaxWidth={700}
                                    getCustomNoAccessErrorStr={selectedHierarchy =>
                                        jobHelper.getAllowJobByHierarchy(selectedHierarchy ? selectedHierarchy.Id : null) ?
                                            null :
                                            strings.Job_is_not_allowed_for_this_hierarchy
                                    }
                                    handleSelectHierarchy={hierarchyId => {
                                        this.originalHierarchyId = this.state.hierarchyId
                                        this.setState({ hierarchyId }, () =>
                                            this.handleUpdateJobData()
                                        )
                                    }}
                                />
                            </div>
                        </div>
                        {AccountStore.getHREnabled() &&
                            <div style={{ width: 350, marginTop: 25 }}>
                                <Toggle
                                    data-intercom-target={"menu-Jobs-Toggle-ShowInESS"}
                                    onToggle={(e, toggled: boolean) => this.setState({ showJobToMobileUsers: toggled }, () => this.handleUpdateJobData(true))}
                                    style={{ width: 250 }}
                                    defaultToggled={this.state.showJobToMobileUsers}
                                    label={ jobHelper.checkJobFieldAvailable('singular') ? `Advertise ${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} To Mobile Users` : strings.ShowJobToMobileUsers}
                                    disabled={this.props.readonly}
                                />
                            </div>
                        }                        
                        {AccountStore.getScheduleEnabled() && AccountStore.getHREnabled() && AccountStore.getCRMEnabled() &&
                            <div style={{ width: 350, marginTop: 25 }}>
                                <Toggle
                                    data-intercom-target={"menu-Jobs-Toggle-AutoAssignShift"}
                                    onToggle={(e, toggled: boolean) => this.setState({ autoAssignEmployees: toggled }, () => this.handleUpdateJobData(true))}
                                    style={{ width: 250 }}
                                    defaultToggled={this.state.autoAssignEmployees}
                                    label={strings.Auto_Assign_Shift}
                                    disabled={this.props.readonly}
                                />
                            </div>
                        }
                        {this.canToggleJobType() &&
                            <div style={{ width: 350, marginTop: 25 }}>
                                <Toggle
                                    disabled={this.props.readonly}
                                    style={{ width: 250 }}
                                    label={strings.Contract}
                                    toggled={this.state.jobType === EJobType.Contract ? true : false}
                                    onToggle={(e, toggled: boolean) => this.setState({ jobType: toggled ? EJobType.Contract : EJobType.NonContract }, () => this.handleUpdateJobData())}
                                />
                            </div>
                        }
                        {AccountStore.getCRMEnabled() && [
                            <div key="first_line" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div key="date" style={{ width: 350 }}>
                                    <Toggle
                                        onToggle={(e, checked) => this.setState({ hasDates: checked }, () => this.handleUpdateJobData(true))}
                                        style={{ paddingTop: 20, width: 250 }}
                                        defaultToggled={this.state.hasDates}
                                        label={strings.Job_Start_End_Date}
                                        disabled={this.props.readonly}
                                    />
                                    {this.state.hasDates &&
                                        <div style={{ width: 350 }}>
                                            {this.state.jobType === EJobType.Contract ?
                                                <DateRangeSelect
                                                    readonly={this.props.readonly}
                                                    startDate={moment(this.state.startDate).utc(true).format()}
                                                    endDate={moment(this.state.endDate).utc(true).format()}
                                                    handleChange={(startDate, endDate, isValid) =>
                                                        isValid &&
                                                        this.setState({ startDate: timeHelper.keepConvertUTCToLocal(startDate), endDate: timeHelper.keepConvertUTCToLocal(endDate) }, () =>
                                                            this.handleUpdateJobData(true)
                                                        )}
                                                /> :
                                                this.state.jobType === EJobType.NonContract &&
                                                <DateTimeRangeSelect
                                                    readonly={this.props.readonly}
                                                    displayTimerOnNextLine
                                                    startDateTime={this.state.startDate}
                                                    endDateTime={this.state.endDate}
                                                    handleChange={(startDate, endDate, isValidDateRange) => {
                                                        this.setState({ startDate, endDate }, () => isValidDateRange && this.handleUpdateJobData(true))
                                                    }}
                                                />
                                            }
                                        </div>
                                    }
                                </div>
                                <div style={{ width: 350, marginTop: 0 }}>
                                    {noteItem}
                                </div>
                                {AccountStore.getHREnabled() &&
                                    statusAndSubStatusItem
                                }
                            </div>,
                            <div key="choose" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <ChooseOccupationChip
                                    readonly={this.props.readonly}
                                    selectedOccupations={this.state.jobOccupations}
                                    handleSave={jobOccupations => {
                                        this.setState({
                                            jobOccupations: [...this.state.jobOccupations, ...jobOccupations]
                                        }, () => this.handleUpdateJobData())
                                    }}
                                    handleDelete={jobOccupations => {
                                        this.setState({
                                            jobOccupations
                                        }, () => this.handleUpdateJobData())
                                    }}
                                />
                                <ChooseSpecialtyChip
                                    readonly={this.props.readonly}
                                    selectedSpecialties={this.state.jobSpecialties}
                                    handleSave={jobSpecialties => {
                                        this.setState({
                                            jobSpecialties: [...this.state.jobSpecialties, ...jobSpecialties]
                                        }, () => this.handleUpdateJobData())
                                    }}
                                    handleDelete={jobSpecialties => {
                                        this.setState({
                                            jobSpecialties
                                        }, () => this.handleUpdateJobData())
                                    }}
                                />
                                <div style={{ paddingTop: 20 }}>
                                    <ChooseBadgeChip
                                        readonly={this.props.readonly}
                                        selectedBadges={this.state.jobBadges}
                                        handleSave={jobBadges => {
                                            this.setState({
                                                jobBadges: [...this.state.jobBadges, ...jobBadges]
                                            }, () => this.handleUpdateJobData())
                                        }}
                                        handleDelete={jobBadges => {
                                            this.setState({
                                                jobBadges
                                            }, () => this.handleUpdateJobData())
                                        }}
                                    />
                                </div>
                            </div>
                        ]}
                    </div>
                }
                <div style={{ position: "absolute", right: 17, top: 10, fontSize: 18 }}>
                    <i
                        data-intercom-target={"menu-Jobs-Btn-ExpandJobInfo"}
                        className={"fa " + (this.state.expanded ? "fa-chevron-up" : "fa-chevron-down") + " itemActionButton"}
                        onClick={() => this.setState({ expanded: !this.state.expanded })}
                    />
                </div>
                {this.state.hierarchyMoveValidationResult.length > 0 &&
                    <JobHierarchyMoveWarningDialog
                        jobName={this.state.jobName.trim()}
                        results={this.state.hierarchyMoveValidationResult}
                        handleProceed={() => {
                            this.setState({ hierarchyMoveValidationResult: [] })
                            this.updateJob()
                        }}
                        handleClose={() => this.setState({ hierarchyMoveValidationResult: [], hierarchyId: this.originalHierarchyId })}
                    />
                }
            </Paper>
        )
    }
}



